import type { Session, User } from '@simplemon/lib';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { type AuthContextType, signIn, signOut, signUp, useSession } from '@/lib/auth-client';

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface ExtendedAuthContextType extends AuthContextType {
  organization: Organization | null;
}

const AuthContext = createContext<ExtendedAuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);

  // Fetch organization only when explicitly requested (lazy loading)
  const fetchOrganization = useCallback(async () => {
    if (!session.data?.user || orgLoading) return;

    setOrgLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/auth/custom/me`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.organization) {
        setOrganization(data.organization);
      }
    } catch (err) {
      console.warn('Failed to fetch organization:', err);
    } finally {
      setOrgLoading(false);
    }
  }, [session.data?.user, orgLoading]);

  // Auto-fetch organization when user is logged in and we're on a protected page
  useEffect(() => {
    if (session.data?.user && !organization && !orgLoading) {
      // Only fetch if we're likely on a protected page (not login/register)
      const isAuthPage =
        window.location.pathname === '/login' || window.location.pathname === '/register';
      if (!isAuthPage) {
        fetchOrganization();
      }
    }
    if (!session.data?.user) {
      setOrganization(null);
    }
  }, [session.data?.user, organization, orgLoading, fetchOrganization]);

  const isLoading = session.isPending;

  const value: ExtendedAuthContextType = {
    user: session.data?.user as User | null,
    session: session.data?.session as Session | null,
    organization,
    isLoading,
    isAuthenticated: !!session.data?.user,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Higher-order component for protected routes
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
    }, []);

    if (!mounted || isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return null;
    }

    return <Component {...props} />;
  };
}
